import {
  type Firestore,
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';

import type {
  BranchProgressCount,
  EmployeeProgressRow,
  PendingRegistrationRow,
  ProgressPage,
  ProgressPort,
  ProgressStage,
} from '../../features/committee/application/ports/progressPort';

/**
 * Firestore implementation of `ProgressPort` (blueprint §11.1).
 *
 * Every read here is bounded. The committee opens this screen often, and ~800
 * employees plus their dependants would otherwise be read in full each time —
 * a cost defect on a billing account with no hard cap (§11.5.1).
 */

/** Firestore's own cap on an `in` clause; also a sane page size. */
const MAX_PAGE_SIZE = 50;

function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export function createFirebaseProgressAdapter(firestore: Firestore): ProgressPort {
  return {
    async listEmployeeProgress({
      branchId,
      employeeNumber,
      nameToken,
      cursor,
      pageSize,
    }): Promise<ProgressPage> {
      const size = Math.min(pageSize, MAX_PAGE_SIZE);

      // A named person is one document, so the page and its cursor do not
      // apply: the search answers with that row or with nothing.
      const searching = employeeNumber !== undefined && employeeNumber.trim() !== '';

      const searchingByName = nameToken !== undefined && nameToken !== '';

      const constraints = [
        ...(searching ? [where('employeeNumber', '==', employeeNumber.trim())] : []),
        ...(searchingByName ? [where('nameTokens', 'array-contains', nameToken)] : []),
        ...(branchId === undefined ? [] : [where('branchId', '==', branchId)]),
        // Ordered by document id so the cursor is stable: ordering by a mutable
        // field would let a row move between pages while someone is reading.
        orderBy('__name__'),
        ...(cursor === undefined ? [] : [startAfter(cursor)]),
        // One extra, to learn whether another page exists without a second
        // query or a count.
        limit(searching ? 1 : size + 1),
      ];

      const snapshot = await getDocs(query(collection(firestore, 'employees'), ...constraints));

      const docs = snapshot.docs.slice(0, size);
      const hasMore = snapshot.docs.length > size;

      const rows: EmployeeProgressRow[] = docs.map((entry) => {
        const data = entry.data();
        const children: unknown = data['declaredChildrenCount'];
        const dependents: unknown = data['dependentsEnteredCount'];

        /*
         * Stage is derived from fields already on the employee document.
         *
         * Reading each employee's declaration to determine it would turn one
         * page into fifty extra reads — and the committee opens this screen
         * many times a day. The trusted write paths keep these counters in step
         * when they change the underlying records.
         */
        const submittedAt = readString(data, 'declarationSubmittedAt');
        const started = data['profileCompletedAt'] !== undefined;
        const stage: ProgressStage = submittedAt
          ? 'submitted'
          : started
            ? 'in_progress'
            : 'not_started';

        return {
          employeeId: entry.id,
          fullName: readString(data, 'fullName') ?? '',
          employeeNumber: readString(data, 'employeeNumber') ?? entry.id,
          branchId: readString(data, 'branchId'),
          stage,
          linked: data['linkedAuthUid'] !== undefined,
          dependentsEntered: typeof dependents === 'number' ? dependents : 0,
          declaredChildrenCount: typeof children === 'number' ? children : 0,
          emailMappingDisputed: data['emailMappingDisputed'] === true,
        };
      });

      return {
        rows,
        nextCursor: hasMore ? (docs[docs.length - 1]?.id ?? null) : null,
      };
    },

    async countByBranch(): Promise<readonly BranchProgressCount[]> {
      /*
       * Aggregation queries, not a full read.
       *
       * `getCountFromServer` is billed at a small fraction of reading the
       * matching documents, which is the difference between a dashboard that
       * costs nothing and one that reads 800 records every time it opens.
       */
      const employees = collection(firestore, 'employees');

      const [total, submitted, started] = await Promise.all([
        getCountFromServer(query(employees)),
        getCountFromServer(query(employees, where('declarationSubmittedAt', '!=', null))),
        getCountFromServer(query(employees, where('profileCompletedAt', '!=', null))),
      ]);

      const totalCount = total.data().count;
      const submittedCount = submitted.data().count;
      const startedCount = started.data().count;

      // A single fund-wide row. Per-branch counts would need one aggregation
      // per branch; that is nine more queries for a number the committee can
      // already see by filtering, so it waits until someone asks for it.
      return [
        {
          branchId: null,
          total: totalCount,
          notStarted: Math.max(0, totalCount - startedCount),
          inProgress: Math.max(0, startedCount - submittedCount),
          submitted: submittedCount,
        },
      ];
    },

    async listPendingRegistrations(): Promise<readonly PendingRegistrationRow[]> {
      const snapshot = await getDocs(
        query(
          collection(firestore, 'registrationRequests'),
          where('status', '==', 'pending'),
          limit(MAX_PAGE_SIZE),
        ),
      );

      return snapshot.docs.map((entry) => {
        const data = entry.data();
        // Firestore's DocumentData is indexed `any`; narrowed before use.
        const name: unknown = data['fullName'];
        const parts =
          typeof name === 'object' && name !== null
            ? (name as Record<string, unknown>)
            : {};

        const joined = ['firstName', 'fatherName', 'grandfatherName', 'familyName']
          .map((key) => (typeof parts[key] === 'string' ? parts[key] : ''))
          .filter(Boolean)
          .join(' ');

        return {
          authUid: entry.id,
          email: readString(data, 'email') ?? '',
          fullNameAr: joined,
          phone: readString(data, 'phone') ?? '',
          branchId: readString(data, 'branchId') ?? '',
          reason:
            readString(data, 'reason') === 'name_mismatch'
              ? 'name_mismatch'
              : 'no_matching_record',
          mismatchedEmployeeId: readString(data, 'mismatchedEmployeeId'),
          submittedAt: readString(data, 'submittedAt') ?? '',
        };
      });
    },
  };
}
